import { AlertTriangle, RefreshCw } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'

/**
 * Empty states always say what is true and how fresh it is ("Nothing owes a reply. Last Desk
 * event 3 min ago."), so empty is distinguishable from broken (scope §5.1).
 */
export function EmptyState({
  icon,
  title,
  detail,
  action,
  className,
}: {
  icon?: React.ReactNode
  title: string
  detail?: React.ReactNode
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      {icon ? <div className="mb-1 text-subtle [&_svg]:size-8">{icon}</div> : null}
      <p className="font-medium text-foreground">{title}</p>
      {detail ? <p className="max-w-sm text-sm text-muted-foreground">{detail}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

export function ErrorBanner({
  tone = 'danger',
  title,
  detail,
  onRetry,
  className,
}: {
  tone?: 'danger' | 'warning'
  title: string
  detail?: React.ReactNode
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2.5 rounded-md border px-3 py-2 text-sm',
        tone === 'danger' ? 'border-destructive/30 bg-destructive-soft text-destructive' : 'border-warning/30 bg-warning-soft text-warning',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{title}</p>
        {detail ? <p className="text-foreground/80">{detail}</p> : null}
      </div>
      {onRetry ? (
        <Button size="sm" variant="ghost" onClick={onRetry} className="text-current">
          <RefreshCw /> Retry
        </Button>
      ) : null}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-md bg-muted', className)} />
}

/** Placeholder rows at the previous count while a lane refreshes. */
export function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="divide-y divide-border">
      {Array.from({ length: count }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders
        <div key={i} className="space-y-2 px-4 py-3">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  )
}

/** Thin top-of-pane bar for stale-while-revalidate ("refreshing", never a blank page). */
export function RefreshBar({ active }: { active: boolean }) {
  if (!active) return null
  return (
    <div role="progressbar" aria-label="Refreshing" className="h-0.5 w-full overflow-hidden bg-primary-soft">
      <div className="h-full w-1/3 animate-pulse bg-primary" />
    </div>
  )
}
